import { Search, X } from 'lucide-react';

import {
	HTMLAttributes,
	forwardRef,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { debounce } from '@/utils/common';
import { cn } from '@/utils/styles';

interface Props extends HTMLAttributes<HTMLDivElement> {
	onSearch: (searchValue: string) => void;
	value?: string;
	header?: string;
	retractable?: boolean;
	ref?: React.RefObject<HTMLInputElement>;
	inputClassName?: string;
	expandedInputClassName?: string;
	alwaysExpandedIfFilled?: boolean;
	debounceMs?: number;
}

const SimpleSearchInput = forwardRef<HTMLInputElement, Props>(
	(
		{
			onSearch,
			value,
			header,
			retractable = false,
			className,
			inputClassName,
			expandedInputClassName,
			alwaysExpandedIfFilled = false,
			debounceMs = 200,
			...otherProps
		},
		ref
	) => {
		const [isExpanded, setIsExpanded] = useState(false);
		const [searchQuery, setSearchQuery] = useState(value ?? '');
		const inputValueRef = useRef(value ?? '');
		const lastEmittedRef = useRef(value ?? '');

		// eslint-disable-next-line react-hooks/exhaustive-deps
		const handleSearch = useCallback(
			debounce((value) => {
				// Drop a pending emit that an external value change (back/forward, clear all) replaced.
				if (value !== inputValueRef.current) return;
				lastEmittedRef.current = value as string;
				onSearch(value as string);
			}, debounceMs),
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[onSearch, debounceMs]
		);

		const updateInput = (next: string) => {
			inputValueRef.current = next;
			setSearchQuery(next);
		};

		const handleClear = () => {
			updateInput('');
			handleSearch('');
		};

		const handleOnChangeSearchQuery = (e: React.ChangeEvent<HTMLInputElement>) => {
			updateInput(e.target.value);
			handleSearch(e.target.value);
		};

		// Follow external value changes, but not the echo of our own emit, which would clobber typing.
		useEffect(() => {
			const external = value ?? '';
			if (external.trim() === lastEmittedRef.current.trim()) return;
			lastEmittedRef.current = external;
			inputValueRef.current = external;
			setSearchQuery(external);
		}, [value]);

		const netExpanded = useMemo(() => {
			if (alwaysExpandedIfFilled && searchQuery && retractable) return true;

			return isExpanded;
		}, [searchQuery, isExpanded, alwaysExpandedIfFilled, retractable]);

		return (
			<div
				className={cn(
					'h-full w-full p-1 transition-all duration-300',
					retractable ? (netExpanded ? '' : '!w-[38px]') : '',
					className
				)}
				{...otherProps}
			>
				{header && (
					<h4
						className="truncate pb-3 text-[15px] font-medium"
						title={header}
					>
						{header}
					</h4>
				)}
				<div className={cn('relative h-full w-full', retractable && !netExpanded ? 'w-fit' : '')}>
					<Input
						type="text"
						placeholder={retractable && !netExpanded ? '' : 'Search...'}
						value={retractable && !netExpanded ? '' : searchQuery}
						onChange={handleOnChangeSearchQuery}
						className={cn(
							'h-9 px-9 text-[13px]',
							retractable && !netExpanded
								? 'w-[38px] cursor-pointer pl-1 pr-1 hover:bg-slate-100'
								: '',
							inputClassName,
							netExpanded && retractable ? expandedInputClassName : ''
						)}
						ref={ref}
						onFocus={() => setIsExpanded(true)}
						onBlur={() => setIsExpanded(false)}
					/>
					<Search
						className={cn(
							'pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 transform text-secondary'
						)}
						size={14}
					/>

					{searchQuery && !(retractable && !netExpanded) && (
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className="absolute right-1 top-1/2 -translate-y-1/2 transform hover:bg-transparent"
							onMouseDown={(e) => {
								e.preventDefault();
								e.stopPropagation();
								handleClear();
							}}
						>
							<X
								size={14}
								className="translate-y-[1px] fill-primary text-primary"
							/>
							<span className="sr-only">Clear search</span>
						</Button>
					)}

					{retractable && searchQuery && !netExpanded && (
						<span className="absolute right-1 top-1 size-1.5 transform rounded-full bg-primary-blue" />
					)}
				</div>
			</div>
		);
	}
);

SimpleSearchInput.displayName = 'SimpleSearchInput';

export default SimpleSearchInput;
